import { ROUTE_TYPES, ROUTE_TYPES_INCLUDING_BOTH } from "@routebot/constants";

const PICKUP_TYPE_VALUES = new Set(["100", ROUTE_TYPES.PICK_UP, ROUTE_TYPES_INCLUDING_BOTH.BOTH]);
const DROPOFF_TYPE_VALUES = new Set(["200", ROUTE_TYPES.DROP_OFF, ROUTE_TYPES_INCLUDING_BOTH.BOTH]);

export function normalizeServiceDateToYmd(dateValue) {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    if (Number.isNaN(dateValue.getTime())) return null;
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const raw = typeof dateValue === "string" ? dateValue : dateValue.toString?.();
  if (!raw || typeof raw !== "string") return null;

  const datePart = raw.trim().split("T")[0].split(" ")[0];
  const parts = datePart.replace(/\//g, "-").split("-");
  if (parts.length < 3) return null;

  const [year, month, day] = parts;
  if (!year || year.length !== 4 || !month || !day) return null;

  const mm = month.padStart(2, "0");
  const dd = String(day)
    .replace(/[^\d].*$/, "")
    .padStart(2, "0");
  if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(mm) || !/^\d{2}$/.test(dd)) return null;

  return `${year}-${mm}-${dd}`;
}

export function isPersonelServiceVisibleOnOrAfter(service, todayYmd) {
  const ymd = normalizeServiceDateToYmd(service?.date);
  if (!ymd || !todayYmd) return true;
  return ymd >= todayYmd;
}

function getServiceTypeValue(service) {
  const type = service?.routeType ?? service?.type;
  return type === undefined || type === null ? "" : type.toString();
}

export function serviceIsPickup(service) {
  const type = getServiceTypeValue(service);
  if (!type) return true;
  return PICKUP_TYPE_VALUES.has(type);
}

export function serviceIsDropOff(service) {
  const type = getServiceTypeValue(service);
  if (!type) return false;
  return DROPOFF_TYPE_VALUES.has(type);
}

function getAssignedDriverIds(service) {
  const raw = service?.driver || service?.drivers;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return [...new Set(list.map((item) => (item?.id || item?._id || item)?.toString?.()).filter(Boolean))];
}

function ensureBucket(index, driverId) {
  if (!index[driverId]) {
    index[driverId] = { pickup: [], dropoff: [] };
  }
  return index[driverId];
}

function addServiceToIndex(index, service, { filterPastDates, todayYmd }) {
  if (!service) return;
  if (filterPastDates && !isPersonelServiceVisibleOnOrAfter(service, todayYmd)) return;

  const name = service.name;
  if (!name) return;

  const driverIds = getAssignedDriverIds(service);
  if (!driverIds.length) return;

  const pickup = serviceIsPickup(service);
  const dropoff = serviceIsDropOff(service);
  if (!pickup && !dropoff) return;

  driverIds.forEach((driverId) => {
    const bucket = ensureBucket(index, driverId);
    if (pickup) bucket.pickup.push(name);
    if (dropoff) bucket.dropoff.push(name);
  });
}

export function buildDriverServiceIndex({
  vehicleRoutes = [],
  bundledRoutes = [],
  standartPersonelServices = [],
  shiftPersonelServices = [],
  todayYmd,
} = {}) {
  const index = {};

  try {
    vehicleRoutes.forEach((service) => addServiceToIndex(index, service, { filterPastDates: false, todayYmd }));
    bundledRoutes.forEach((service) => addServiceToIndex(index, service, { filterPastDates: false, todayYmd }));
    standartPersonelServices.forEach((service) =>
      addServiceToIndex(index, service, { filterPastDates: true, todayYmd }),
    );
    shiftPersonelServices.forEach((service) => addServiceToIndex(index, service, { filterPastDates: true, todayYmd }));
  } catch {
    /* defensive: never break the grid over a malformed row */
  }

  return index;
}

export function getDriverServiceNames(index, driverId, direction) {
  const bucket = index?.[driverId?.toString()];
  if (!bucket) return [];
  return direction === "DROP_OFF" ? bucket.dropoff : bucket.pickup;
}
